import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { Appearance } from "common/generator/components/button-appearance";
import type { WireTitleComponent } from "common/generator/components/fluent-components";
import type { GlideFC } from "lib/glide-fc";
import React from "react";
import tw from "twin.macro";
import { css, styled } from "webapp/styled-components";
import { breakoutActions, extractActions, makeActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";
import Img from "../../smart/img/img";
import { WireButton } from "../wire-button/wire-button";
import { WireMenuButton } from "../wire-menu-button/wire-menu-button";
import type { HeroProps } from "./wire-hero-lib";

const Style = styled.div`
    &.style-image.none .text-block {
        text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.35), 0px 1px 2px rgba(0, 0, 0, 0.5);
    }
`;

const Hero: GlideFC<HeroProps> = p => {
    const { backgroundEffect, description, emphasizedTitle, iconImageURL, imageCorners, menuItems, size, title } = p;

    const [primary, secondary, ...menuActions] = breakoutActions(menuItems, 2);
    const hasSecondary = secondary !== undefined;
    const hasMenu = menuActions.length > 0;
    const hasActions = primary !== undefined || secondary !== undefined || hasMenu;

    return (
        <Style
            className={classNames(
                size,
                backgroundEffect?.toLowerCase(),
                imageCorners,
                !hasSecondary && "no-secondary",
                !hasMenu && "no-menu"
            )}
            css={css`
                &.size-sm {
                    ${tw`text-xl min-height[2rem]
                      gp-md:(text-2xl min-height[2rem])
                      gp-lg:(text-3xl min-height[2rem])`}
                }
                &.size-md {
                    ${tw`text-2xl min-height[2rem]
                      gp-md:(text-3xl min-height[2rem])
                      gp-lg:(text-4xl min-height[2rem])`}
                }
                &.size-lg {
                    ${tw`text-3xl min-height[5rem]
                      gp-md:(text-4xl min-height[8rem])
                      gp-lg:(text-5xl min-height[12.5rem])`}
                }
            `}
            tw="relative py-5 flex flex-col justify-center flex-grow font-extrabold leading-snug text-4xl
                min-height[8rem]
                gp-sm:py-5
                gp-md:(py-6 text-5xl rounded-xl min-height[12rem])
                gp-lg:(text-6xl flex-row items-center min-height[16rem])
                gp-xl:py-8">
            <div
                tw="w-full grid grid-template-columns[auto minmax(0, 1fr) auto] grid-template-rows[auto minmax(0, 1fr)]
                    gp-lg:mr-8">
                <div
                    css={css`
                        .size-lg &,
                        .size-xl & {
                            ${tw`grid-row-start[1] grid-row-end[-1]`}
                        }
                    `}>
                    {iconImageURL && (
                        <Img
                            src={massageImageUrl(
                                iconImageURL,
                                {
                                    thumbnail: false,
                                    width: 480,
                                    height: 480,
                                },
                                undefined
                            )}
                            css={css`
                                .circle &&&&&&& {
                                    border-radius: 9999px;
                                }
                                .size-sm & {
                                    ${tw`w-14 h-14 mr-4 rounded
                                      gp-md:(w-20 h-20 mr-6)
                                      gp-lg:(w-28 h-28)`}
                                }
                                .size-md & {
                                    ${tw`w-20 h-20 mr-4
                                      gp-md:(w-24 h-24 mr-6)
                                      gp-lg:(w-32 h-32)`}
                                }
                                .size-lg & {
                                    ${tw`w-24 h-24 mr-6
                                      gp-md:(w-32 h-32 mr-8)
                                      gp-lg:(w-48 h-48)`}
                                }
                            `}
                            tw="w-28 h-28 flex-shrink-0 self-start object-cover rounded-lg mr-6
                                gp-md:(w-48 h-48 rounded-xl mr-8)
                                gp-lg:(w-60 h-60)"
                        />
                    )}
                </div>
                <div
                    className={classNames(!hasActions && "no-actions")}
                    css={css`
                        .size-lg &.no-actions,
                        .size-xl &.no-actions {
                            ${tw`row-span-full`}
                        }
                    `}
                    tw="flex flex-col justify-center">
                    {emphasizedTitle && (
                        <h3
                            tw="font-bold font-size[12px] leading-loose uppercase text-text-contextual-accent"
                            className={classNames("text-accent", "text-block")}>
                            {emphasizedTitle}
                        </h3>
                    )}
                    {title && (
                        <h2
                            className={classNames("text-title", "text-block")}
                            tw="leading-none mb-2 last:mb-0 text-text-contextual-dark">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p
                            className="text-description text-block"
                            css={css`
                                .size-sm &,
                                .size-md & {
                                    ${tw`gp-md:text-base`}
                                }
                                .size-lg & {
                                    ${tw`text-base
                                      gp-lg:text-lg`}
                                }
                            `}
                            tw="font-normal whitespace-pre-wrap text-base text-text-contextual-dark
                                gp-md:text-lg">
                            {description}
                        </p>
                    )}
                </div>
                {hasActions && (
                    <div
                        css={css`
                            .size-sm &,
                            .size-md & {
                                ${tw`items-center
                                  gp-sm:col-start-2
                                  gp-md:(col-auto mt-0)`}
                            }
                        `}
                        tw="col-span-full items-start mt-4 space-x-2 flex
                            gp-sm:col-start-2">
                        {primary !== undefined && (
                            <WireButton
                                size={size === "size-xl" ? "md" : "sm"}
                                className="primary-button"
                                tw="flex-1
                                    gp-sm:flex-initial"
                                {...makeActionSpreadProps(primary)}
                                appearance={Appearance.Filled}
                            />
                        )}
                        {secondary !== undefined && (
                            <WireButton
                                size={size === "size-xl" ? "md" : "sm"}
                                appearance={Appearance.Bordered}
                                {...makeActionSpreadProps(secondary)}
                            />
                        )}
                        {hasMenu && (
                            <WireMenuButton
                                size={size === "size-xl" ? "md" : "sm"}
                                menuItems={menuActions}
                                appearance={Appearance.Bordered}
                            />
                        )}
                    </div>
                )}
            </div>
        </Style>
    );
};

export const WireHero: WireRenderer<Omit<WireTitleComponent, "backgroundImage" | "withBlur">> = React.memo(p => {
    const { size, image, subtitle, title, titleEmphasized, backgroundEffect, imageCorners, actions, backend } = p;
    return (
        <Hero
            size={size}
            title={title ?? undefined}
            description={subtitle ?? undefined}
            iconImageURL={image ?? undefined}
            emphasizedTitle={titleEmphasized ?? undefined}
            backgroundEffect={backgroundEffect}
            imageCorners={imageCorners}
            menuItems={extractActions(actions, backend)}
        />
    );
});

export default Hero;
